import { useState } from "react";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { useDataStore } from "@store/user_org";
import { useAtelierStore } from "@store/atelierStore";
import { atelierApi, type RetagAutreItem, type RetagAutreResult } from "@services/api/atelier";
import { isOwnerOrAdmin } from "@utils/permissions";
import { showNotification } from "context/notification.service";

const formatDate = (iso?: string) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const formatHours = (h: number) => `${+(h || 0).toFixed(2)} h`;

function ItemRow({ item }: { item: RetagAutreItem }) {
  const head = [item.ordre_num ? `OR ${item.ordre_num}` : "", item.client_name, item.plate].filter(Boolean).join(" · ");
  const tail = [item.worker_name, formatDate(item.date), formatHours(item.hours),
    item.action === "fusionnee" ? "fusionnée dans la charge Mécanique" : ""].filter(Boolean).join(" · ");
  return (
    <div className="flex flex-col py-1.5">
      <span className="text-h4 font-medium text-neutral-700">{head || "Sans ordre"}</span>
      <span className="text-h5 text-neutral-400">{tail}</span>
    </div>
  );
}

/** Réparation : les travaux posés sur un mécanicien pendant qu'il était classé « Autre »
 *  (rôle chef d'atelier, octobre 2026) restaient cachés sous le filtre Mécanique. */
export function RetagAutreCard() {
  const userData = useDataStore((s) => s.userData);
  const orgData = useDataStore((s) => s.orgData);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<RetagAutreResult | null>(null);
  const [confirming, setConfirming] = useState(false);

  if (!isOwnerOrAdmin(userData)) return null;

  const run = async (dryRun: boolean) => {
    setLoading(true);
    try {
      const result = await atelierApi.retagAutreToMecanique(dryRun);
      setPreview(result);
      if (!dryRun) {
        showNotification({
          type: "success",
          message: `${result.jobs_count} travau${result.jobs_count > 1 ? "x" : ""} et ${result.charges_count} charge${result.charges_count > 1 ? "s" : ""} repassés en Mécanique`,
        });
        if (orgData?.id) useAtelierStore.getState().fetchData(orgData.id, true);
      }
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Échec de l'opération" });
    } finally {
      setLoading(false);
    }
  };

  const nothing = preview && preview.jobs_count === 0 && preview.charges_count === 0;

  return (
    <div className="flex flex-col gap-3 bg-white p-4 rounded-lg border-1 max-w-700">
      <div className="flex flex-col">
        <div className="text-h3 font-semibold text-black">
          Planning atelier : repasser en Mécanique les travaux classés «&nbsp;Autre&nbsp;»
        </div>
        <div className="text-h4 text-neutral-400">
          Travaux posés sur un mécanicien mais enregistrés en «&nbsp;Autre&nbsp;» (invisibles
          avec le filtre Mécanique), et leurs charges à répartir. Les lignes MyO
          (AUTRE, TAKATA, LOCATION) ne sont pas touchées.
        </div>
      </div>
      <div className="flex flex-row gap-2">
        <SecondaryButton variant="small" label={loading ? "En cours..." : "Aperçu"} disabled={loading} onClick={() => run(true)} />
        <Button
          variant="small"
          label="Appliquer"
          disabled={loading || !preview?.dry_run || !!nothing}
          onClick={() => setConfirming(true)}
        />
      </div>
      {preview && (
        <div className="flex flex-col gap-1">
          <div className="text-h4 font-medium text-neutral-600">
            {nothing
              ? "Rien à corriger."
              : `${preview.dry_run ? "À corriger" : "Corrigé"} : ${preview.jobs_count} travau${preview.jobs_count > 1 ? "x" : ""}, ${preview.charges_count} charge${preview.charges_count > 1 ? "s" : ""}${preview.charges_merged ? ` (dont ${preview.charges_merged} fusionnée${preview.charges_merged > 1 ? "s" : ""})` : ""}.`}
          </div>
          {preview.jobs.length > 0 && (
            <div className="flex flex-col divide-y max-h-300 overflow-auto">
              {preview.jobs.map((j) => <ItemRow key={j.id} item={j} />)}
            </div>
          )}
          {preview.charges.length > 0 && (
            <>
              <div className="text-h4 font-medium text-neutral-600 mt-2">Charges à répartir</div>
              <div className="flex flex-col divide-y">
                {preview.charges.map((c) => <ItemRow key={c.id} item={c} />)}
              </div>
            </>
          )}
        </div>
      )}
      {confirming && preview && (
        <ConfirmationModal
          title="Repasser ces travaux en Mécanique ?"
          message={`${preview.jobs_count} travau${preview.jobs_count > 1 ? "x" : ""} et ${preview.charges_count} charge${preview.charges_count > 1 ? "s" : ""} seront reclassés en Mécanique sur le planning atelier.`}
          confirmLabel="Appliquer"
          onClose={() => setConfirming(false)}
          onConfirm={() => { setConfirming(false); run(false); }}
        />
      )}
    </div>
  );
}
